const STORAGE_KEY = "convey.liveShareAgencyByConversation";

function loadPersistedAgencies() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map<string, string>();
    return new Map<string, string>(Object.entries(JSON.parse(raw)));
  } catch {
    return new Map<string, string>();
  }
}

const liveShareAgencyByConversation = loadPersistedAgencies();

function persistLiveShareAgencies() {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Object.fromEntries(liveShareAgencyByConversation))
    );
  } catch {
    // Ignore quota / private-mode failures.
  }
}

function pickConversationId(event: any): string | null {
  if (!event || typeof event !== "object") return null;
  return (
    event.conversation_id ||
    event.conversationId ||
    event.callId ||
    event.payload?.object?.conversation_id ||
    event.payload?.conversation_id ||
    event.object?.conversation_id ||
    null
  );
}

function pickAgencyId(event: any): string | null {
  if (!event || typeof event !== "object") return null;
  return (
    event.agency_id ||
    event.agencyId ||
    event.payload?.object?.agency_id ||
    event.payload?.agency_id ||
    event.object?.agency_id ||
    null
  );
}

export function rememberLiveShareAgency(conversationId?: string, agencyId?: string) {
  if (conversationId && agencyId) {
    liveShareAgencyByConversation.set(conversationId, agencyId);
    persistLiveShareAgencies();
  }
}

export function getLiveShareAgencyId(conversationId?: string) {
  if (!conversationId) return null;
  return liveShareAgencyByConversation.get(conversationId) || null;
}

export function extractConversationEventContext(eventName: string, payload: any) {
  const events = Array.isArray(payload) ? payload : [payload];
  const results: Array<{ conversationId: string; agencyId: string; event: any }> = [];

  events.forEach((event) => {
    if (!event || typeof event !== "object") return;
    const conversationId = pickConversationId(event);
    const agencyId = pickAgencyId(event) || getLiveShareAgencyId(conversationId);
    if (conversationId && agencyId) {
      rememberLiveShareAgency(conversationId, agencyId);
      results.push({ conversationId, agencyId, event });
    }
  });

  return results;
}

export function isForeignAgencyEvent(agencyId?: string, selfAgencyId?: string) {
  return Boolean(agencyId && selfAgencyId && agencyId !== selfAgencyId);
}

export function conversationNeedsHydration(conversation: any) {
  if (!conversation) return true;
  if (!conversation.agency_id) return true;
  if (conversation.channel === "voice" && !conversation.metadata?.correlationId) return true;
  return false;
}

export type SipChannelKind = "sip" | "sip:stable" | "sipAudio" | "sipSentiment";

export function parseSipChannelEvent(
  eventName: string
): { kind: SipChannelKind; agencyId: string; correlationId: string } | null {
  if (!eventName || typeof eventName !== "string") return null;
  const patterns: Array<[SipChannelKind, RegExp]> = [
    ["sip:stable", /^sip:stable:([^:]+):(.+)$/],
    ["sipAudio", /^sipAudio:([^:]+):(.+)$/],
    ["sipSentiment", /^sipSentiment:([^:]+):(.+)$/],
    ["sip", /^sip:([^:]+):(.+)$/]
  ];
  for (const [kind, re] of patterns) {
    const match = eventName.match(re);
    if (match) {
      return { kind, agencyId: match[1], correlationId: match[2] };
    }
  }
  return null;
}

export function sipEventName(kind: SipChannelKind, agencyId: string, correlationId: string) {
  switch (kind) {
    case "sip:stable":
      return `sip:stable:${agencyId}:${correlationId}`;
    case "sipAudio":
      return `sipAudio:${agencyId}:${correlationId}`;
    case "sipSentiment":
      return `sipSentiment:${agencyId}:${correlationId}`;
    default:
      return `sip:${agencyId}:${correlationId}`;
  }
}

export function eventMatchesConversation(
  data: any,
  conversationId?: string,
  correlationId?: string
) {
  if (!data || typeof data !== "object") return true;
  const eventConversationId = data.conversationId || data.conversation_id || data.callId;
  if (conversationId && eventConversationId && eventConversationId !== conversationId) {
    return false;
  }
  const eventCorrelationId = data.correlationId || data.correlation_id;
  if (correlationId && eventCorrelationId && eventCorrelationId !== correlationId) {
    return false;
  }
  return true;
}

export function sipRelayMatches(
  eventName: string,
  data: any,
  conversationId?: string,
  correlationId?: string
) {
  const parsed = parseSipChannelEvent(eventName);
  const eventConversationId = data?.conversationId || data?.conversation_id || data?.callId;
  if (conversationId && eventConversationId) {
    return eventConversationId === conversationId;
  }
  if (parsed && correlationId) {
    return parsed.correlationId === correlationId;
  }
  const eventCorrelationId = data?.correlationId || data?.correlation_id;
  if (correlationId && eventCorrelationId) {
    return eventCorrelationId === correlationId;
  }
  return eventName === "interpretation" && Boolean(conversationId && eventConversationId);
}
